/**
 * QuickActionsFab — sticky "quick actions" launcher, bottom-right of the shell.
 *
 * BOLT-392. Studios reported click fatigue reaching the pages they edit most.
 * The worst offender is Programs: `/programs` has no top-nav entry anywhere in
 * the portal, and its only link is an accordion row inside the Website page
 * (WebsitePage.tsx, "Go to Programs page →"). This puts all five destinations
 * one click from any screen.
 *
 * Visibility is per-item, not per-role-block: each action carries its own
 * predicate and the launcher renders only when at least one survives. Today
 * that means staff see nothing — every destination either hard-redirects them
 * (`/website`, `/programs`, `/org-settings` all `<Navigate to="/">` for staff)
 * or depends on `GET /v1/orgs/:id`, which is
 * requireRole('org_owner','org_admin','super_admin') and 403s for staff. The
 * per-item shape means the launcher appears on its own if that ever changes,
 * rather than needing this component rewritten.
 *
 * Super admins pass the predicate only while they hold org context; without an
 * orgId every destination redirects to /admin/orgs, so the list empties and
 * nothing renders.
 */
import { useState } from 'react';
import {
  Box,
  Fab,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { Link } from 'react-router';
import { useAuthStore } from '@/store/auth.js';
import { useOrgFullSettings, type OrgFullResponse } from '@/lib/api/org-settings.js';

// ---------------------------------------------------------------------------
// Live-site URL resolution
// ---------------------------------------------------------------------------

/**
 * Mirrors WebsitePage's precedence exactly: an org in `external` website mode
 * publishes `external_website_url` (already absolute); everything else serves
 * `website_url` as a bare hostname needing the scheme prepended. Returns null
 * when the org has no resolvable site — the "View website" action then renders
 * as an inert row rather than pointing at `https://undefined`.
 *
 * Note the external branch does NOT fall through to `website_url`: an
 * external-mode org's CloudFront host is not the site the studio publishes.
 */
function resolveLiveSiteUrl(data: OrgFullResponse | undefined): string | null {
  if (!data) return null;
  if (data.website_mode === 'external') return data.external_website_url;
  return data.website_url ? `https://${data.website_url}` : null;
}

// ---------------------------------------------------------------------------
// Action definitions
// ---------------------------------------------------------------------------

interface QuickAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Internal SPA route. Mutually exclusive with `href`. */
  to?: string;
  /** External URL, opened in a new tab. Mutually exclusive with `to`. */
  href?: string;
  /** Renders as an inert row — present but not actionable. */
  disabled?: boolean;
  /** Second line, explaining why a disabled row is disabled. */
  secondary?: string;
}

export function QuickActionsFab() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  const role = useAuthStore((s) => s.role);
  const orgId = useAuthStore((s) => s.orgId);

  // The single entitlement behind every destination below: each page either
  // enforces org_owner|org_admin at the API, or redirects everyone else.
  // A super_admin qualifies only while scoped to an org.
  const canManageOrg =
    role === 'org_owner' || role === 'org_admin' || (role === 'super_admin' && !!orgId);

  // Only fetch when the answer can matter — a staff session must not fire a
  // request that is guaranteed to 403.
  const orgFullQuery = useOrgFullSettings(orgId ?? '', canManageOrg && !!orgId);
  const liveSiteUrl = resolveLiveSiteUrl(orgFullQuery.data);

  const actions: QuickAction[] = [];
  if (canManageOrg) {
    actions.push({
      key: 'programs',
      label: 'Modify programs',
      icon: <ListAltOutlinedIcon fontSize="small" />,
      to: '/programs',
    });
    actions.push({
      key: 'website',
      label: 'Modify website',
      icon: <LanguageOutlinedIcon fontSize="small" />,
      to: '/website',
    });
    // Always present, so the menu does not silently change length while a
    // studio is mid-provisioning. Without a resolvable site the row stays but
    // goes inert and says why — a studio that just signed up would otherwise
    // see four items with nothing to explain the fifth's absence.
    // While the org record is still in flight we know nothing yet, so the row
    // is inert but makes no claim: asserting "not ready" during load would
    // flash a false statement at every studio that does have a site.
    actions.push(
      liveSiteUrl
        ? {
            key: 'view-website',
            label: 'View website',
            icon: <OpenInNewOutlinedIcon fontSize="small" />,
            href: liveSiteUrl,
          }
        : {
            key: 'view-website',
            label: 'View website',
            icon: <OpenInNewOutlinedIcon fontSize="small" />,
            disabled: true,
            ...(orgFullQuery.isLoading ? {} : { secondary: 'Website not ready yet' }),
          },
    );
    actions.push({
      key: 'notifications',
      label: 'Update notifications',
      icon: <NotificationsOutlinedIcon fontSize="small" />,
      to: '/org-settings?tab=notifications',
    });
    actions.push({
      key: 'org-settings',
      label: 'Modify organization settings',
      icon: <BusinessOutlinedIcon fontSize="small" />,
      to: '/org-settings',
    });
  }

  // Nothing this user can reach — render no launcher at all rather than an
  // empty menu that opens onto blank space.
  if (actions.length === 0) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 16, sm: 24 },
          right: { xs: 16, sm: 24 },
          // Above page content but below MUI's own modal/menu layer, so the
          // menu this button opens still paints over it.
          zIndex: (t) => t.zIndex.speedDial,
        }}
      >
        <Tooltip title={open ? '' : 'Quick actions'} placement="left">
          <Fab
            color="primary"
            aria-label="quick actions"
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={(e) => setAnchor(e.currentTarget)}
            sx={{ boxShadow: 3 }}
          >
            <KeyboardArrowDownIcon
              sx={{
                // One icon, rotated — the down chevron becomes the up chevron
                // and the transition is the affordance.
                transition: 'transform 0.2s ease',
                transform: open ? 'rotate(180deg)' : 'none',
              }}
            />
          </Fab>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: { mb: 1.5, minWidth: 260, borderRadius: 2 },
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            px: 2,
            pt: 0.5,
            pb: 1,
            color: 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          Quick actions
        </Typography>

        {actions.map((action) =>
          action.disabled ? (
            // No `component` — an inert row must not be a link. MUI's disabled
            // prop stops pointer events but an <a> would still be keyboard-
            // focusable and followable, which is exactly the dead navigation
            // this row exists to prevent.
            <MenuItem key={action.key} disabled sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
              <ListItemText primary={action.label} secondary={action.secondary} />
            </MenuItem>
          ) : action.href ? (
            <MenuItem
              key={action.key}
              component="a"
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setAnchor(null)}
              sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
              <ListItemText primary={action.label} />
            </MenuItem>
          ) : (
            <MenuItem
              key={action.key}
              component={Link}
              to={action.to!}
              onClick={() => setAnchor(null)}
              sx={{ borderRadius: 1, mx: 0.5, px: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
              <ListItemText primary={action.label} />
            </MenuItem>
          ),
        )}
      </Menu>
    </>
  );
}
